import React, { useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Checkbox,
  Grid,
} from "@mui/material";
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  DescriptionOutlined as ReportIcon,
  Group,
} from "@mui/icons-material";
import { BRAND } from "../../components/auth/authTheme";
import MobileMonthYearFilters from "../../components/mobile/MobileMonthYearFilters";

const NAVY = BRAND.navy;
const ORANGE = BRAND.orangeHover;
const BG = "#F8FAFC";

const StatCard = ({ icon, label, value, iconBg, iconColor }) => (
  <Box
    sx={{
      bgcolor: "#fff",
      borderRadius: "12px",
      px: 0.75,
      py: 1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
      gap: 0.5,
      border: `1px solid ${BRAND.border}`,
      height: "100%",
      minWidth: 0,
    }}
  >
    <Box
      sx={{
        width: 30,
        height: 30,
        borderRadius: "8px",
        bgcolor: iconBg,
        color: iconColor,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {icon}
    </Box>
    <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: NAVY, lineHeight: 1 }}>
      {value}
    </Typography>
    <Typography
      sx={{
        fontSize: "0.625rem",
        color: BRAND.muted,
        fontWeight: 500,
        lineHeight: 1.25,
        whiteSpace: "normal",
        wordBreak: "break-word",
        width: "100%",
      }}
    >
      {label}
    </Typography>
  </Box>
);

const ReportCard = ({
  employee,
  isSelected,
  onToggleSelect,
  onDownloadPF,
  onDownloadESIC,
  canExport,
}) => {
  const fullName = `${employee.first_name || ""} ${employee.last_name || ""}`.trim();

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        borderRadius: "12px",
        mb: 1.25,
        border: isSelected ? `2px solid ${ORANGE}` : `1px solid ${BRAND.border}`,
        boxShadow: "0 1px 6px rgba(26, 43, 74, 0.04)",
        overflow: "hidden",
      }}
    >
      <Box sx={{ px: 1.25, py: 0.875 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: canExport ? "24px 1fr auto" : "1fr auto",
            alignItems: "center",
            columnGap: 0.75,
            mb: 0.35,
          }}
        >
          {canExport && (
            <Checkbox
              size="small"
              checked={isSelected}
              onChange={() => onToggleSelect(employee)}
              sx={{
                p: 0,
                color: BRAND.muted,
                "&.Mui-checked": { color: ORANGE },
              }}
              inputProps={{ "aria-label": `Select ${fullName}` }}
            />
          )}
          <Typography
            sx={{
              fontWeight: 700,
              color: NAVY,
              fontSize: "0.875rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              minWidth: 0,
            }}
          >
            {fullName || "Unknown"}
          </Typography>
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={() => onDownloadPF(employee)}
              aria-label="Download PF ECR"
              sx={{
                p: 0,
                color: ORANGE,
                bgcolor: BRAND.orangeLight,
                width: 32,
                height: 32,
              }}
            >
              <DownloadIcon sx={{ fontSize: "1rem" }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => onDownloadESIC(employee)}
              aria-label="Download ESIC"
              sx={{
                p: 0,
                color: "#2196f3",
                bgcolor: "rgba(33, 150, 243, 0.08)",
                width: 32,
                height: 32,
              }}
            >
              <DownloadIcon sx={{ fontSize: "1rem" }} />
            </IconButton>
          </Box>
        </Box>
        <Typography
          sx={{
            fontSize: "0.6875rem",
            fontWeight: 600,
            color: ORANGE,
            pl: canExport ? "calc(24px + 6px)" : 0,
          }}
        >
          {employee.employee_id || "N/A"}
        </Typography>
        <Typography
          sx={{
            fontSize: "0.625rem",
            color: BRAND.muted,
            pl: canExport ? "calc(24px + 6px)" : 0,
            mt: 0.25,
          }}
        >
          PF ECR · ESIC
        </Typography>
      </Box>
    </Box>
  );
};

const ReportsMobileView = ({
  employees,
  searchTerm,
  setSearchTerm,
  selectedMonth,
  setSelectedMonth,
  selectedYear,
  setSelectedYear,
  monthOptions,
  yearOptions,
  periodLabel,
  selectedRows,
  onToggleSelect,
  onBulkPF,
  onBulkESIC,
  onDownloadPF,
  onDownloadESIC,
  canExport,
}) => {
  const selectedIds = useMemo(
    () => new Set(selectedRows.map((row) => row.id)),
    [selectedRows]
  );

  const filteredList = useMemo(() => {
    if (!searchTerm.trim()) return employees;
    const q = searchTerm.toLowerCase();
    return employees.filter(
      (emp) =>
        emp.first_name?.toLowerCase().includes(q) ||
        emp.last_name?.toLowerCase().includes(q) ||
        emp.employee_id?.toLowerCase().includes(q)
    );
  }, [employees, searchTerm]);

  return (
    <Box sx={{ pb: 10, pt: 1, px: 2, bgcolor: BG, minHeight: "100vh" }}>
      <Typography sx={{ fontSize: "1.25rem", fontWeight: 800, color: NAVY, mb: 0.25 }}>
        Reports
      </Typography>
      <Typography sx={{ fontSize: "0.8125rem", color: BRAND.muted, mb: 1.5 }}>
        Download PF ECR & ESIC for {periodLabel}
      </Typography>

      <MobileMonthYearFilters
        sx={{ mb: 1.5 }}
        selectedMonth={selectedMonth}
        selectedYear={selectedYear}
        onMonthChange={setSelectedMonth}
        onYearChange={setSelectedYear}
        monthOptions={monthOptions}
        yearOptions={yearOptions}
      />

      <Grid container spacing={1} sx={{ mb: 1.5 }}>
        <Grid item xs={6} sx={{ minWidth: 0 }}>
          <StatCard
            icon={<Group sx={{ fontSize: "1rem" }} />}
            label="Employees"
            value={filteredList.length}
            iconBg="rgba(33,150,243,0.15)"
            iconColor="#2196f3"
          />
        </Grid>
        <Grid item xs={6} sx={{ minWidth: 0 }}>
          <StatCard
            icon={<ReportIcon sx={{ fontSize: "1rem" }} />}
            label="Selected"
            value={selectedRows.length}
            iconBg="rgba(230,90,0,0.12)"
            iconColor={ORANGE}
          />
        </Grid>
      </Grid>

      {canExport && selectedRows.length > 0 && (
        <Box
          sx={{
            mb: 1.5,
            p: 1.25,
            bgcolor: BRAND.orangeLight,
            borderRadius: "12px",
            border: `1px solid ${ORANGE}33`,
          }}
        >
          <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: NAVY, mb: 1 }}>
            {selectedRows.length} selected — download bulk reports
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Box
              component="button"
              type="button"
              onClick={onBulkPF}
              sx={{
                flex: 1,
                border: "none",
                borderRadius: "10px",
                bgcolor: ORANGE,
                color: "#fff",
                fontWeight: 600,
                fontSize: "0.75rem",
                py: 1,
                cursor: "pointer",
              }}
            >
              PF ECR
            </Box>
            <Box
              component="button"
              type="button"
              onClick={onBulkESIC}
              sx={{
                flex: 1,
                border: "none",
                borderRadius: "10px",
                bgcolor: NAVY,
                color: "#fff",
                fontWeight: 600,
                fontSize: "0.75rem",
                py: 1,
                cursor: "pointer",
              }}
            >
              ESIC
            </Box>
          </Box>
        </Box>
      )}

      <TextField
        fullWidth
        placeholder="Search employees..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{
          mb: 2,
          "& .MuiOutlinedInput-root": {
            bgcolor: "#fff",
            borderRadius: "12px",
            "& fieldset": { borderColor: BRAND.border },
            "&.Mui-focused fieldset": { borderColor: ORANGE },
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: BRAND.muted }} />
            </InputAdornment>
          ),
        }}
      />

      {filteredList.length > 0 ? (
        filteredList.map((emp) => (
          <ReportCard
            key={emp.id}
            employee={emp}
            isSelected={selectedIds.has(emp.id)}
            onToggleSelect={onToggleSelect}
            onDownloadPF={onDownloadPF}
            onDownloadESIC={onDownloadESIC}
            canExport={canExport}
          />
        ))
      ) : (
        <Box
          sx={{
            textAlign: "center",
            py: 6,
            px: 2,
            bgcolor: "#fff",
            borderRadius: "16px",
            border: `1px solid ${BRAND.border}`,
          }}
        >
          <ReportIcon sx={{ fontSize: 48, color: "#CBD5E1", mb: 1 }} />
          <Typography sx={{ fontWeight: 600, color: NAVY, mb: 0.5 }}>
            No employees found
          </Typography>
          <Typography sx={{ fontSize: "0.875rem", color: BRAND.muted }}>
            Adjust your search or month filter.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ReportsMobileView;
